import { useQuery } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";

import { SignInLink } from "@/components/auth/sign-in-link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/components/ui/lib/utils";
import { useRevokeSession } from "@/hooks/auth/use-revoke-session";
import { useSwitchSession } from "@/hooks/auth/use-switch-session";
import type { Session, User } from "@/lib/auth";
import { listDeviceSessions, useSession } from "@/lib/auth-client";
import * as m from "@/paraglide/messages";

export type AccountSwitcherSession = {
	session: Pick<Session, "id" | "token" | "expiresAt">;
	user: Pick<User, "id" | "name" | "email" | "image">;
};

export type AccountSwitcherProps = {
	sessions: AccountSwitcherSession[];
	currentSessionToken?: string;
	onSwitchSession?: (sessionToken: string) => void;
	onRevokeSession?: (sessionToken: string) => void;
	signInRender?: React.ReactElement;
};

// Helper function to get initials from name or email
function getInitials(name?: string | null, email?: string | null) {
	if (name) {
		const parts = name.split(" ");
		if (parts.length >= 2) {
			return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
		}
		return name.slice(0, 2).toUpperCase();
	}
	if (email) {
		return email.slice(0, 2).toUpperCase();
	}
	return "??";
}

// Color palette for avatar backgrounds
const avatarColors = [
	"bg-blue-500",
	"bg-green-500",
	"bg-orange-500",
	"bg-purple-500",
	"bg-pink-500",
	"bg-cyan-500",
	"bg-indigo-500",
	"bg-teal-500",
];

function getColorForId(id: string) {
	let hash = 0;
	for (let i = 0; i < id.length; i++) {
		hash = (hash << 5) - hash + id.charCodeAt(i);
		hash |= 0;
	}
	return avatarColors[Math.abs(hash) % avatarColors.length];
}

export function AccountSwitcher() {
	const { data: currentSession } = useSession();
	const { mutate: switchSession } = useSwitchSession();
	const { mutate: revokeSession } = useRevokeSession();

	const { data: sessions } = useQuery({
		queryKey: ["multiSession", "listDeviceSessions"],
		queryFn: async () => {
			const result = await listDeviceSessions({}, { throw: true });

			return result;
		},
	});

	return (
		<AccountSwitcherContent
			currentSessionToken={currentSession?.session?.token}
			onRevokeSession={revokeSession}
			onSwitchSession={switchSession}
			sessions={sessions ?? []}
		/>
	);
}

export function AccountSwitcherContent({
	sessions,
	currentSessionToken,
	onSwitchSession,
	signInRender,
}: AccountSwitcherProps) {
	const resolvedSignInRender = signInRender ?? <SignInLink />;

	return (
		<>
			<DropdownMenuSeparator />
			<DropdownMenuGroup className="space-y-1">
				<DropdownMenuLabel>{m.account_switch()}</DropdownMenuLabel>
				{sessions.map((session) => {
					const isActive = session.session.token === currentSessionToken;
					const user = session.user;

					return (
						<DropdownMenuItem
							key={session.session.id || session.session.expiresAt.toISOString()}
							className={cn(
								"flex items-center gap-3 py-1 group/session",
								isActive && "bg-muted/70 ring-1 ring-primary/20",
							)}
							aria-current={isActive ? "true" : undefined}
							onClick={() => {
								if (!isActive) {
									onSwitchSession?.(session.session.token);
								}
							}}
						>
							<Avatar className="h-8 w-8">
								{user.image ? <AvatarImage src={user.image} /> : null}
								<AvatarFallback
									className={cn(
										"text-white text-xs font-medium",
										getColorForId(user.id),
									)}
								>
									{getInitials(user.name, user.email)}
								</AvatarFallback>
							</Avatar>
							<div className="flex flex-col flex-1 min-w-0">
								<span className="text-sm font-medium truncate">
									{user.name || user.email}
								</span>
								{user.name && user.email && (
									<span className="text-xs text-muted-foreground truncate">
										{user.email}
									</span>
								)}
							</div>
							{isActive && (
								<Badge variant="secondary" className="shrink-0 text-[11px]">
									{m.accountSettings_current()}
								</Badge>
							)}
						</DropdownMenuItem>
					);
				})}
				<DropdownMenuItem className="flex items-center gap-2" render={resolvedSignInRender}>
					<div className="flex h-5 w-5 items-center justify-center rounded-full border-[1.5px] border-dashed border-muted-foreground/50">
						<PlusIcon className="h-3 w-3 text-muted-foreground" />
					</div>
					<span className="text-xs">{m.account_signInMore()}</span>
				</DropdownMenuItem>
			</DropdownMenuGroup>
		</>
	);
}
